import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteBackground,
  fetchBackgroundImageObjectUrl,
  fetchConfigYaml,
  listBackgrounds,
  setDefaultBackground,
  uploadBackground,
} from '../api';
import { NONE_BACKGROUND, type InteriorBackground, type InteriorConfigYaml } from '../types';
import { createLogger } from '../../utils/logger';
import { AdminAction } from '../../components/PermissionGate';

const log = createLogger('InteriorSettingsPage');

type Tab = 'backgrounds' | 'config';

/**
 * Settings page for the interior service.
 *
 * Two tabs:
 *
 * - **Backgrounds** — manage the set of background images the apollo
 *   cropper composites behind the vehicle interior (interior-scoped
 *   mirror of lightning's photobooths). Includes the interior-only
 *   "No background" option, which leaves the original image background
 *   untouched (`replace_background: false`).
 * - **Pipeline Config** — read-only view of `config_interior.yaml`
 *   loaded at startup. Changing values requires editing the file +
 *   restarting the service.
 */
export function InteriorSettingsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('backgrounds');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ----- Backgrounds tab state --------------------------------------------
  const [backgrounds, setBackgrounds] = useState<InteriorBackground[]>([]);
  // name -> object URL for the (auth-fetched) thumbnail. The protected
  // image route can't be hit by a raw <img src> behind the API gateway,
  // so we fetch each thumbnail with auth and render the blob URL.
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [bgLoading, setBgLoading] = useState(true);
  const [bgError, setBgError] = useState<string | null>(null);
  const [uploadName, setUploadName] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ----- Config tab state -------------------------------------------------
  const [config, setConfig] = useState<InteriorConfigYaml | null>(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const flashSuccess = useCallback((msg: string) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 2500);
  }, []);

  const loadBackgrounds = useCallback(async () => {
    setBgLoading(true);
    try {
      const data = await listBackgrounds();
      setBackgrounds(data);
      setBgError(null);
    } catch (ex) {
      setBgError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBgLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBackgrounds();
  }, [loadBackgrounds]);

  // Fetch thumbnails (with auth) whenever the set of real backgrounds
  // changes. Keyed on the names only so toggling the default doesn't
  // refetch. Object URLs are revoked on cleanup to avoid leaks.
  const bgNamesKey = backgrounds
    .filter((b) => b.name !== NONE_BACKGROUND)
    .map((b) => b.name)
    .join(',');
  useEffect(() => {
    const names = bgNamesKey.split(',').filter(Boolean);
    if (names.length === 0) {
      setThumbs({});
      return;
    }
    let cancelled = false;
    const created: string[] = [];
    Promise.all(
      names.map(async (name) => {
        try {
          const url = await fetchBackgroundImageObjectUrl(name);
          created.push(url);
          return [name, url] as const;
        } catch (ex) {
          log.error(`Failed to load thumbnail for '${name}': ${ex}`);
          return [name, ''] as const;
        }
      }),
    ).then((entries) => {
      if (cancelled) {
        created.forEach((u) => URL.revokeObjectURL(u));
        return;
      }
      setThumbs(Object.fromEntries(entries.filter(([, u]) => u)));
    });
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [bgNamesKey]);

  useEffect(() => {
    if (activeTab !== 'config' || config) return;
    let cancelled = false;
    setConfigLoading(true);
    fetchConfigYaml()
      .then((d) => {
        if (cancelled) return;
        setConfig(d);
        setConfigError(null);
      })
      .catch((ex) => {
        if (!cancelled) setConfigError(ex instanceof Error ? ex.message : String(ex));
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, config]);

  const handleUpload = useCallback(async () => {
    const name = uploadName.trim();
    const file = fileInputRef.current?.files?.[0];
    if (!name || !file) {
      setBgError('Provide both a name and an image file.');
      return;
    }
    setUploading(true);
    setBgError(null);
    try {
      await uploadBackground(name, file);
      log.info(`Uploaded interior background '${name}'`);
      setUploadName('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadBackgrounds();
      flashSuccess(`Background "${name}" uploaded`);
    } catch (ex) {
      setBgError(ex instanceof Error ? ex.message : 'Failed to upload background');
    } finally {
      setUploading(false);
    }
  }, [uploadName, loadBackgrounds, flashSuccess]);

  const handleDelete = useCallback(
    async (name: string) => {
      if (!confirm(`Delete background "${name}"?`)) return;
      setBgError(null);
      try {
        await deleteBackground(name);
        await loadBackgrounds();
        flashSuccess(`Background "${name}" deleted`);
      } catch (ex) {
        setBgError(ex instanceof Error ? ex.message : 'Failed to delete background');
      }
    },
    [loadBackgrounds, flashSuccess],
  );

  const handleSetDefault = useCallback(
    async (name: string) => {
      setBgError(null);
      try {
        await setDefaultBackground(name);
        await loadBackgrounds();
        flashSuccess(
          name === NONE_BACKGROUND
            ? 'Default set to "No background"'
            : `"${name}" set as default`,
        );
      } catch (ex) {
        setBgError(ex instanceof Error ? ex.message : 'Failed to set default');
      }
    },
    [loadBackgrounds, flashSuccess],
  );

  const handleCopy = useCallback(async () => {
    if (!config) return;
    try {
      await navigator.clipboard.writeText(config.yaml);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable; ignore
    }
  }, [config]);

  const realBackgrounds = backgrounds.filter((b) => b.name !== NONE_BACKGROUND);
  const noneEntry = backgrounds.find((b) => b.name === NONE_BACKGROUND);

  return (
    <div className="space-y-4 max-w-5xl">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Interior Settings</h1>
        <p className="text-sm text-slate-500 mt-1">
          Manage background images and inspect the apollo cropper pipeline configuration.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setActiveTab('backgrounds')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'backgrounds'
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Backgrounds
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('config')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
            activeTab === 'config'
              ? 'border-indigo-500 text-indigo-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Pipeline Config
        </button>
      </div>

      {successMessage && (
        <div className="rounded-xl border border-success-200 bg-success-50 px-3 py-2 text-sm text-success-700">
          {successMessage}
        </div>
      )}

      {activeTab === 'backgrounds' && (
        <div className="space-y-5">
          {bgError && (
            <div className="rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
              {bgError}
            </div>
          )}

          {/* Upload form */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-soft p-5">
            <h2 className="text-base font-semibold text-slate-900 mb-1">Add a background</h2>
            <p className="text-sm text-slate-500 mb-4">
              Upload a PNG or JPEG. The image is used as a composition reference behind the
              vehicle interior for every scan that selects it.
            </p>
            <div className="flex flex-col sm:flex-row sm:items-end gap-3">
              <div className="flex-1">
                <label
                  htmlFor="bg-name"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Name
                </label>
                <input
                  id="bg-name"
                  type="text"
                  value={uploadName}
                  onChange={(e) => setUploadName(e.target.value)}
                  placeholder="e.g., studio_white"
                  className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500 text-sm"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Letters, numbers, hyphens and underscores only.
                </p>
              </div>
              <div className="flex-1">
                <label
                  htmlFor="bg-file"
                  className="block text-sm font-medium text-slate-700 mb-1.5"
                >
                  Image file
                </label>
                <input
                  id="bg-file"
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-600 hover:file:bg-indigo-100"
                />
              </div>
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading}
                className="btn-primary px-4 py-2 text-sm whitespace-nowrap disabled:opacity-50"
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>

          {/* Background grid */}
          {bgLoading ? (
            <div className="text-sm text-slate-400">Loading backgrounds…</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {/* No-background (interior-only) card */}
              <div
                className={`rounded-2xl border bg-white shadow-soft overflow-hidden flex flex-col ${
                  noneEntry?.is_default ? 'border-indigo-400 ring-1 ring-indigo-200' : 'border-slate-200'
                }`}
              >
                <div className="aspect-square bg-slate-50 flex items-center justify-center text-slate-300">
                  <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <div className="p-3 flex flex-col gap-2 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-slate-900 truncate">No background</span>
                    {noneEntry?.is_default && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] leading-snug text-slate-400">
                    The original image background will not be changed.
                  </p>
                  <div className="mt-auto pt-1">
                    {!noneEntry?.is_default && (
                      <button
                        type="button"
                        onClick={() => handleSetDefault(NONE_BACKGROUND)}
                        className="w-full text-xs px-2 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                      >
                        Set as default
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {realBackgrounds.map((bg) => (
                <div
                  key={bg.name}
                  className={`rounded-2xl border bg-white shadow-soft overflow-hidden flex flex-col ${
                    bg.is_default ? 'border-indigo-400 ring-1 ring-indigo-200' : 'border-slate-200'
                  }`}
                >
                  <div className="aspect-square bg-slate-100">
                    {thumbs[bg.name] ? (
                      <img
                        src={thumbs[bg.name]}
                        alt={bg.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300 text-xs">
                        Loading…
                      </div>
                    )}
                  </div>
                  <div className="p-3 flex flex-col gap-2 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-medium text-slate-900 truncate" title={bg.name}>
                        {bg.name}
                      </span>
                      {bg.is_default && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
                          Default
                        </span>
                      )}
                    </div>
                    <div className="mt-auto pt-1 flex gap-2">
                      {!bg.is_default && (
                        <button
                          type="button"
                          onClick={() => handleSetDefault(bg.name)}
                          className="flex-1 text-xs px-2 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
                        >
                          Set default
                        </button>
                      )}
                      <AdminAction>
                        <button
                          type="button"
                          onClick={() => handleDelete(bg.name)}
                          className="text-xs px-2 py-1.5 rounded-lg border border-danger-200 text-danger-700 hover:bg-danger-50"
                        >
                          Delete
                        </button>
                      </AdminAction>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'config' && (
        <div className="space-y-3">
          <p className="text-sm text-slate-500">
            Read-only view of the apollo cropper pipeline configuration loaded at startup. To
            change values, edit{' '}
            <code className="font-mono text-xs px-1 py-0.5 rounded bg-slate-100">
              config_interior.yaml
            </code>{' '}
            and restart the service.
          </p>
          {configLoading && <div className="text-sm text-slate-400">Loading…</div>}
          {configError && (
            <div className="rounded-xl border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700">
              {configError}
            </div>
          )}
          {config && (
            <div className="bg-white border border-slate-200 rounded-xl shadow-soft overflow-hidden">
              <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-slate-50">
                <div className="text-xs font-mono text-slate-500 truncate">{config.path}</div>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="px-2 py-1 rounded text-xs font-medium text-slate-700 hover:bg-white"
                >
                  {copied ? 'Copied!' : 'Copy YAML'}
                </button>
              </div>
              <pre className="px-4 py-3 text-xs font-mono text-slate-800 whitespace-pre overflow-x-auto max-h-[70vh] overflow-y-auto leading-relaxed">
                {config.yaml}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
